/** A press becomes a submission, and a refusal comes back to the person who pressed.
 *
 * `Convention_ASubmissionFailureMustReachThePersonWhoPressedIt`. A control that fails silently is
 * the defect this file exists to prevent, so nothing here ever discards a promise: every call has
 * exactly one place its failure lands, and that place is the control that caused it.
 */

import type { GateMode } from "./contract";
import { GATE_MODE_HIDE, GATE_MODE_SHOW_LOCKED } from "./contract";
import type { PressAction } from "./controlTree";

/**
 * The one call a control makes. The host supplies it, because this half holds no HTTP client and
 * must not learn one: an overlay canvas, a companion page and a test all reach the submission path
 * differently and all of them draw the same controls.
 */
export type SubmitPress = (press: PressAction) => Promise<unknown>;

/** How far an event stamp may sit from the wall clock before it is treated as nonsense. */
const PRESS_TIME_SANITY_MS = 60_000;

const TIME_ORIGIN = typeof performance === "undefined" ? Number.NaN : performance.timeOrigin;

/** When the person pressed, as `Envelope.occurred_at`, read from the event's own high-res stamp.
 *
 * An absent, non-finite or wildly out-of-range stamp falls back to the wall clock.
 */
export function pressedAt(timeStamp: number | undefined): string {
  const monotonic = monotonicPress(timeStamp);
  if (monotonic === undefined) return new Date(Date.now()).toISOString();
  return new Date(TIME_ORIGIN + monotonic).toISOString();
}

/** An event stamp that really is on `performance.now()`, or undefined when it is not. */
export function monotonicPress(timeStamp: number | undefined): number | undefined {
  if (timeStamp === undefined || !Number.isFinite(timeStamp) || !Number.isFinite(TIME_ORIGIN)) {
    return undefined;
  }
  if (Math.abs(TIME_ORIGIN + timeStamp - Date.now()) > PRESS_TIME_SANITY_MS) return undefined;
  return timeStamp;
}

/** The last state frame this host took, and when it took it: the origin a press counts from.
 *
 * `receivedAt` is a `performance.now()` reading, the same clock a DOM event's `timeStamp` is on.
 */
export interface FrameStamp {
  readonly epoch: string;
  readonly sequence: number;
  /** The cloud instant the frame named, echoed unchanged. */
  readonly sentAt: string;
  /** `performance.now()` when this host applied that frame. */
  readonly receivedAt: number;
}

/** A press as `SubmitRequest.occurred_after`: which frame, and how long after taking it. */
export interface PressOffset {
  readonly epoch: string;
  readonly sequence: number;
  readonly sent_at: string;
  readonly elapsed_ms: number;
}

/** The one press time a submission body carries. Exactly one of the two is ever set. */
export interface PressTime {
  readonly occurred_at: string | undefined;
  readonly occurred_after: PressOffset | undefined;
}

/** The offset when a frame stamp is held and the press is on the same clock, the wall clock else. */
export function pressTime(frame: FrameStamp | undefined, timeStamp: number | undefined): PressTime {
  const monotonic = monotonicPress(timeStamp);
  if (frame !== undefined && monotonic !== undefined) {
    const elapsed = Math.round(monotonic - frame.receivedAt);
    if (elapsed >= 0) {
      return {
        occurred_at: undefined,
        occurred_after: {
          epoch: frame.epoch,
          sequence: frame.sequence,
          sent_at: frame.sentAt,
          elapsed_ms: elapsed,
        },
      };
    }
  }
  return { occurred_at: pressedAt(timeStamp), occurred_after: undefined };
}

/** Why a press did not count, in the words the person who pressed is owed. */
export interface Refusal {
  /** One line to show on the control. Never empty. */
  readonly message: string;
  /** The permit names that would have met the gate, empty when the refusal was not a gate. */
  readonly missing: readonly string[];
  /** The host's own words for the lock, empty when the host wrote none. */
  readonly label: string;
  /** What losing the permit does to this control, when the refusal named it. */
  readonly mode: GateMode | undefined;
}

/** The last thing said about a press, which is never that it worked.
 *
 * `sending` is optimistic feedback and it claims nothing: it says the press was received here, not
 * that it counted anywhere. There is therefore nothing to reconcile when the authoritative
 * projection arrives, because nothing was asserted about the outcome in the first place.
 */
export type PressState =
  | { readonly kind: "sending" }
  | { readonly kind: "refused"; readonly refusal: Refusal };

const REFUSED_WITHOUT_A_REASON = "that did not count";

interface Detailed {
  readonly message?: unknown;
  readonly detail?: unknown;
}

function readDetail(raised: unknown): Record<string, unknown> {
  if (typeof raised !== "object" || raised === null) return {};
  const detail = (raised as Detailed).detail;
  if (typeof detail !== "object" || detail === null || Array.isArray(detail)) return {};
  return detail as Record<string, unknown>;
}

function readString(detail: Record<string, unknown>, key: string): string {
  const held = detail[key];
  return typeof held === "string" ? held : "";
}

/** The api joins the missing permit names with a comma, because a detail body carries strings. */
function readMissing(detail: Record<string, unknown>): readonly string[] {
  return readString(detail, "missing")
    .split(",")
    .map((name) => name.trim())
    .filter((name) => name.length > 0);
}

function readMode(detail: Record<string, unknown>): GateMode | undefined {
  const held = readString(detail, "mode");
  if (held === GATE_MODE_HIDE) return GATE_MODE_HIDE;
  if (held === GATE_MODE_SHOW_LOCKED) return GATE_MODE_SHOW_LOCKED;
  return undefined;
}

function readMessage(raised: unknown): string {
  if (raised instanceof Error && raised.message.length > 0) return raised.message;
  if (typeof raised === "string" && raised.length > 0) return raised;
  return REFUSED_WITHOUT_A_REASON;
}

/**
 * Read whatever came back from a refused submission into something a control can draw.
 *
 * A gate refusal carries the missing permits, the host's own label and the mode, because a refusal
 * is the exact moment a canvas has to say what would unlock the control. The label wins the one
 * line the control shows: it is the customer's language, and the platform ships none of its own.
 */
export function refusalOf(raised: unknown): Refusal {
  const detail = readDetail(raised);
  const label = readString(detail, "label");

  return {
    message: label.length > 0 ? label : readMessage(raised),
    missing: readMissing(detail),
    label,
    mode: readMode(detail),
  };
}
